import React, { useState, useEffect } from 'react';
import { Alert, Button, Card, Form } from 'react-bootstrap';

import {
  CreateUpdateExamDto,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import FormRow from '../../../app/components/FormRow';
import ValidationAlert from '../../../app/components/ValidationAlert';
import ConfirmationButton from '../../../app/components/ConfirmationButton';
import momencik from '../../../common/momencik';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { useNavigation } from '../../../common/navigation';
import { useAuth } from '../../../common/hooks/useAuth';
import { Role } from '../../../common/spdCore/authorization/roles';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import CurrencyControl from '../../../app/components/CurrencyControl';
import { validatePayment } from '../../../common/spdCore/contexts/ExamContext';

import { PaymentTypeSelector } from './PaymentTypeSelector';
import { SellDocumentTypeSelector } from './SellDocumentTypeSelector';

interface ExamPaymentEditorProps {
  exam: CreateUpdateExamDto;
  examId?: string;
  onCompleted?: (exam: CreateUpdateExamDto) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const ExamPaymentEditor: React.FC<ExamPaymentEditorProps> = ({
  exam,
  examId,
  onCompleted,
}) => {
  const spd = useSpdCore();
  const nav = useNavigation();
  const auth = useAuth();
  const [examData, setExamData] = useState<CreateUpdateExamDto>(exam);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showInvoiceError, setShowInvoiceError] = useState(false);
  const [invoiceErrorMessages, setInvoiceErrorMessages] = useState<string[]>(
    [],
  );

  useEffect(() => {
    setExamData(exam);
  }, [exam]);

  const handleSave = async () => {
    const validation = await validatePayment(examData);
    if (!validation.valid) {
      setSaveErrors(validation.errors);
      setShowSaveErrors(true);
      setShowSuccess(false);
      return;
    }
    try {
      if (onCompleted) {
        const result = await onCompleted(examData);
        if (!result.saved) {
          setSaveErrors(result.errors);
          setShowSaveErrors(true);
          setShowSuccess(false);
        } else {
          setShowSuccess(true);
          setShowSaveErrors(false);
        }
      }
    } catch {
      setSaveErrors(['Błąd podczas zapisywania danych']);
      setShowSaveErrors(true);
      setShowSuccess(false);
    }
  };

  const propertyChange = (obj: Partial<CreateUpdateExamDto>) => {
    setExamData({ ...examData, ...obj });
  };

  const handleCreateInvoice = async () => {
    if (!examId) {
      return;
    }
    try {
      const createdInvoice = await spd.salesInvoices.createFromExam(examId);
      nav.salesInvoiceEditor(createdInvoice.id);
    } catch (response) {
      const errors = await responseErrors(response as ResponseError);
      setInvoiceErrorMessages(errors);
      setShowInvoiceError(true);
    }
  };

  return (
    <div className="d-flex justify-content-center mb-3">
      <Card className="exam-payment-card">
        <Card.Header>
          <Card.Title className="mb-0">Podsumowanie płatności</Card.Title>
        </Card.Header>
        <Card.Body>
          <Form className="d-grid gap-3">
            <PaymentTypeSelector
              value={examData.paymentType ?? undefined}
              onChange={paymentType => propertyChange({ paymentType })}
            />

            <FormRow controlId="paid" label="Zapłacono">
              <CurrencyControl
                value={examData.paid || ''}
                decimalScale={2}
                onChange={value => propertyChange({ paid: value || null })}
              />
            </FormRow>

            <SellDocumentTypeSelector
              value={examData.sellDocumentType ?? undefined}
              onChange={sellDocumentType =>
                propertyChange({ sellDocumentType })
              }
            />

            <FormRow controlId="paymentDate" label="Zapłacono dnia">
              <Form.Control
                type="date"
                max={momencik(new Date(), 'YYYY-MM-DD')}
                value={momencik(examData.paymentDate, 'YYYY-MM-DD')}
                onChange={e =>
                  propertyChange({
                    paymentDate: e.target.value
                      ? new Date(e.target.value)
                      : null,
                  })
                }
              />
            </FormRow>
          </Form>
          {showSaveErrors && (
            <ValidationAlert
              errors={saveErrors}
              show={showSaveErrors}
              className="mb-3 mt-3"
            />
          )}
          <ValidationAlert
            show={showInvoiceError}
            modal={true}
            onHide={() => setShowInvoiceError(false)}
            errors={invoiceErrorMessages}
          />
          {showSuccess && (
            <Alert variant="success" className="mb-3 mt-3">
              Dane płatności zostały zaktualizowane
            </Alert>
          )}
          <Button onClick={handleSave} className="w-100 mt-3">
            Zapisz
          </Button>
          {exam.sellDocumentType !== 'Faktura' &&
            auth.check([Role.Finance, Role.Admin, Role.BasicFinance]) && (
              <Alert variant="info" className="mb-0 mt-2">
                Po zapisaniu płatności z typem dokumentu &quot;Faktura&quot;
                możliwe będzie wystawienie faktury do badania.
              </Alert>
            )}
          {examId &&
            exam.sellDocumentType === 'Faktura' &&
            auth.check([Role.Finance, Role.Admin, Role.BasicFinance]) && (
              <ConfirmationButton
                confirmation="Czy chcesz wystawić fakturę dla tego badania?"
                variant="outline-primary"
                onOK={handleCreateInvoice}
                className="w-100 mt-2"
              >
                Wystaw fakturę
              </ConfirmationButton>
            )}
        </Card.Body>
      </Card>
    </div>
  );
};
